import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSiteStore } from '../stores/siteStore';
import { benchmarkingAPI } from '../utils/api';
import {
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  MinusIcon,
  ScaleIcon,
} from '@heroicons/react/24/outline';

const TABS = [
  { id: 'sites', label: 'Site Comparison' },
  { id: 'shifts', label: 'Shift Analysis' },
  { id: 'periods', label: 'Period Comparison' },
  { id: 'zones', label: 'Zone Rankings' },
  { id: 'contractors', label: 'Contractors' },
];

function DeltaIndicator({ delta }) {
  if (!delta) return null;
  const { percentage, direction, isSignificant } = delta;
  const isUp = direction === 'up';
  const color = isSignificant ? (isUp ? 'text-red-500' : 'text-emerald-500') : 'text-slate-400';
  const Icon = isUp ? ArrowTrendingUpIcon : direction === 'down' ? ArrowTrendingDownIcon : MinusIcon;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-semibold ${color}`}>
      <Icon className="w-3 h-3" />
      {Math.abs(percentage)}%
    </span>
  );
}

function ComplianceDelta({ delta }) {
  if (!delta) return null;
  const { percentage, direction, isSignificant } = delta;
  // For compliance, up is good
  const isGood = direction === 'up';
  const color = isSignificant ? (isGood ? 'text-emerald-500' : 'text-red-500') : 'text-slate-400';
  const Icon = direction === 'up' ? ArrowTrendingUpIcon : direction === 'down' ? ArrowTrendingDownIcon : MinusIcon;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-semibold ${color}`}>
      <Icon className="w-3 h-3" />
      {Math.abs(percentage)}%
    </span>
  );
}

function GradeBadge({ grade }) {
  const colors = {
    A: 'bg-emerald-100 text-emerald-700',
    B: 'bg-blue-100 text-blue-700',
    C: 'bg-amber-100 text-amber-700',
    D: 'bg-red-100 text-red-700',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${colors[grade] || colors.C}`}>
      {grade}
    </span>
  );
}

export default function BenchmarkingPage() {
  const { selectedSite, sites } = useSiteStore();
  const siteId = selectedSite?.id;
  const [activeTab, setActiveTab] = useState('sites');
  const [comparison, setComparison] = useState('week');

  // Site Comparison
  const { data: siteComparison } = useQuery({
    queryKey: ['benchmarking-sites'],
    queryFn: () => benchmarkingAPI.getSiteComparison({ period: '30d' }).then(r => r.data),
    enabled: activeTab === 'sites',
  });

  // Shift Analysis
  const { data: shiftData } = useQuery({
    queryKey: ['benchmarking-shifts', siteId],
    queryFn: () => benchmarkingAPI.getShiftAnalysis({ siteId, period: '30d' }).then(r => r.data),
    enabled: activeTab === 'shifts' && !!siteId,
  });

  // Period Comparison
  const { data: periodData } = useQuery({
    queryKey: ['benchmarking-period', siteId, comparison],
    queryFn: () => benchmarkingAPI.getPeriodComparison({ siteId, comparison }).then(r => r.data),
    enabled: activeTab === 'periods' && !!siteId,
  });

  // Zone Benchmarking
  const { data: zoneData } = useQuery({
    queryKey: ['benchmarking-zones', siteId],
    queryFn: () => benchmarkingAPI.getZoneBenchmarking({ siteId, period: '30d' }).then(r => r.data),
    enabled: activeTab === 'zones' && !!siteId,
  });

  // Contractor Comparison
  const { data: contractorData } = useQuery({
    queryKey: ['benchmarking-contractors', siteId],
    queryFn: () => benchmarkingAPI.getContractorComparison({ siteId, period: '30d' }).then(r => r.data),
    enabled: activeTab === 'contractors',
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-800">Benchmarking</h1>
        <p className="text-sm text-slate-500 mt-1">Compare performance across sites, shifts, periods, and zones</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 rounded-lg p-1 overflow-x-auto">
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
              activeTab === tab.id ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Site Comparison Tab */}
      {activeTab === 'sites' && (
        <div className="space-y-4">
          {siteComparison?.sites?.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {siteComparison.sites.map(s => (
                  <div key={s.site.id} className="glass p-4">
                    <h3 className="font-semibold text-slate-700 mb-3">{s.site.name}</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-500">Compliance</span>
                        <span className="font-bold text-slate-700">{s.metrics.avgCompliance}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Alert Rate</span>
                        <span className="font-medium text-slate-700">{s.metrics.alertRate}/1000hrs</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Critical Alerts</span>
                        <span className="font-medium text-red-600">{s.metrics.criticalAlerts}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Incidents</span>
                        <span className="font-medium text-slate-700">{s.metrics.totalIncidents}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Avg Response</span>
                        <span className="font-medium text-slate-700">{s.metrics.avgResponseTime}s</span>
                      </div>
                      <div className="pt-2 border-t border-slate-200 flex items-center gap-2">
                        <span className="text-xs text-slate-400">Compliance trend:</span>
                        <span className={`text-xs font-semibold ${
                          s.complianceTrend === 'improving' ? 'text-emerald-600' :
                          s.complianceTrend === 'declining' ? 'text-red-600' : 'text-slate-500'
                        }`}>
                          {s.complianceTrend}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Divergences */}
              {siteComparison.divergences?.length > 0 && (
                <div className="glass p-4">
                  <h3 className="text-sm font-semibold text-slate-700 mb-3">Trend Divergences</h3>
                  <div className="space-y-2">
                    {siteComparison.divergences.map((d, i) => (
                      <div key={i} className="flex items-center gap-2 p-2 bg-amber-50 rounded-lg text-sm">
                        <ScaleIcon className="w-4 h-4 text-amber-500" />
                        <span className="text-slate-700">
                          <strong>{d.sites[0]}</strong> vs <strong>{d.sites[1]}</strong> — {d.metric} gap of <strong>{d.difference}%</strong>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="glass p-8 text-center">
              <p className="text-slate-400">Need at least 2 active sites for comparison</p>
            </div>
          )}
        </div>
      )}

      {/* Shift Analysis Tab */}
      {activeTab === 'shifts' && (
        <div className="space-y-4">
          {!siteId ? (
            <div className="glass p-8 text-center"><p className="text-slate-400">Select a site</p></div>
          ) : shiftData?.shifts?.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {shiftData.shifts.map(shift => (
                  <div key={shift.shift} className="glass p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold text-slate-700 capitalize">{shift.shift} Shift</h3>
                      <GradeBadge grade={shift.grade} />
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-500">Total Alerts</span>
                        <span className="font-bold text-slate-700">{shift.totalAlerts}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Alert Rate</span>
                        <span className="font-medium text-slate-700">{shift.alertRate}/1000hrs</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Critical</span>
                        <span className="font-medium text-red-600">{shift.criticalAlerts}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {shiftData.recommendations?.length > 0 && (
                <div className="glass p-4">
                  <h3 className="text-sm font-semibold text-slate-700 mb-3">Recommendations</h3>
                  <div className="space-y-2">
                    {shiftData.recommendations.map((rec, i) => (
                      <div key={i} className={`p-3 rounded-lg text-sm ${
                        rec.type === 'warning' ? 'bg-amber-50 text-amber-700' : 'bg-blue-50 text-blue-700'
                      }`}>
                        {rec.message}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="glass p-8 text-center"><p className="text-slate-400">No shift data available</p></div>
          )}
        </div>
      )}

      {/* Period Comparison Tab */}
      {activeTab === 'periods' && (
        <div className="space-y-4">
          {!siteId ? (
            <div className="glass p-8 text-center"><p className="text-slate-400">Select a site</p></div>
          ) : (
            <>
              <div className="flex gap-2">
                {['week', 'month'].map(c => (
                  <button
                    key={c}
                    onClick={() => setComparison(c)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                      comparison === c ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600'
                    }`}
                  >
                    {c === 'week' ? 'Week-over-Week' : 'Month-over-Month'}
                  </button>
                ))}
              </div>

              {periodData && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="glass p-4">
                    <p className="text-xs text-slate-500 mb-1">Total Alerts</p>
                    <div className="flex items-baseline gap-2">
                      <span className="text-2xl font-black text-slate-800">{periodData?.current?.totalAlerts}</span>
                      <DeltaIndicator delta={periodData?.deltas?.totalAlerts} />
                    </div>
                    <p className="text-xs text-slate-400 mt-1">Previous: {periodData?.previous?.totalAlerts}</p>
                  </div>
                  <div className="glass p-4">
                    <p className="text-xs text-slate-500 mb-1">Compliance</p>
                    <div className="flex items-baseline gap-2">
                      <span className="text-2xl font-black text-slate-800">{periodData?.current?.avgCompliance}%</span>
                      <ComplianceDelta delta={periodData?.deltas?.avgCompliance} />
                    </div>
                    <p className="text-xs text-slate-400 mt-1">Previous: {periodData?.previous?.avgCompliance}%</p>
                  </div>
                  <div className="glass p-4">
                    <p className="text-xs text-slate-500 mb-1">Alert Rate</p>
                    <div className="flex items-baseline gap-2">
                      <span className="text-2xl font-black text-slate-800">{periodData?.current?.alertRate}</span>
                      <DeltaIndicator delta={periodData?.deltas?.alertRate} />
                    </div>
                    <p className="text-xs text-slate-400 mt-1">per 1000 man-hours</p>
                  </div>
                  <div className="glass p-4">
                    <p className="text-xs text-slate-500 mb-1">Critical Alerts</p>
                    <div className="flex items-baseline gap-2">
                      <span className="text-2xl font-black text-slate-800">{periodData?.current?.criticalAlerts}</span>
                      <DeltaIndicator delta={periodData?.deltas?.criticalAlerts} />
                    </div>
                    <p className="text-xs text-slate-400 mt-1">Previous: {periodData?.previous?.criticalAlerts}</p>
                  </div>
                  <div className="glass p-4">
                    <p className="text-xs text-slate-500 mb-1">Incidents</p>
                    <div className="flex items-baseline gap-2">
                      <span className="text-2xl font-black text-slate-800">{periodData?.current?.totalIncidents}</span>
                      <DeltaIndicator delta={periodData?.deltas?.totalIncidents} />
                    </div>
                    <p className="text-xs text-slate-400 mt-1">Previous: {periodData?.previous?.totalIncidents}</p>
                  </div>
                  <div className="glass p-4">
                    <p className="text-xs text-slate-500 mb-1">Man-Hours</p>
                    <div className="flex items-baseline gap-2">
                      <span className="text-2xl font-black text-slate-800">{periodData?.current?.totalManHours}</span>
                    </div>
                    <p className="text-xs text-slate-400 mt-1">Previous: {periodData?.previous?.totalManHours}</p>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Zone Rankings Tab */}
      {activeTab === 'zones' && (
        <div>
          {!siteId ? (
            <div className="glass p-8 text-center"><p className="text-slate-400">Select a site</p></div>
          ) : (
            <div className="glass p-4">
              {zoneData?.topPerformer && (
                <div className="flex gap-4 mb-4 flex-wrap">
                  <div className="px-3 py-2 rounded-lg bg-emerald-50 text-sm">
                    <span className="text-emerald-600 font-semibold">Top Performer:</span> {zoneData.topPerformer.zone} (Score: {zoneData.topPerformer.score})
                  </div>
                  {zoneData.needsAttention && (
                    <div className="px-3 py-2 rounded-lg bg-red-50 text-sm">
                      <span className="text-red-600 font-semibold">Needs Attention:</span> {zoneData.needsAttention.zone} (Score: {zoneData.needsAttention.score})
                    </div>
                  )}
                </div>
              )}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500">Rank</th>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500">Zone</th>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500">Type</th>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500">Risk Level</th>
                      <th className="text-right py-2 px-3 text-xs font-semibold text-slate-500">Alerts</th>
                      <th className="text-right py-2 px-3 text-xs font-semibold text-slate-500">Violations</th>
                      <th className="text-right py-2 px-3 text-xs font-semibold text-slate-500">Daily Rate</th>
                      <th className="text-right py-2 px-3 text-xs font-semibold text-slate-500">Risk-Adjusted Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(zoneData?.zones || []).map((z, i) => (
                      <tr key={z.zone.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                        <td className="py-2 px-3 font-bold text-slate-400">#{i + 1}</td>
                        <td className="py-2 px-3 font-medium text-slate-700">{z.zone.name}</td>
                        <td className="py-2 px-3 text-slate-500 capitalize">{z.zone.type}</td>
                        <td className="py-2 px-3">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                            z.zone.riskLevel === 'critical' ? 'bg-red-100 text-red-700' :
                            z.zone.riskLevel === 'high' ? 'bg-orange-100 text-orange-700' :
                            z.zone.riskLevel === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'
                          }`}>
                            {z.zone.riskLevel}
                          </span>
                        </td>
                        <td className="py-2 px-3 text-right text-slate-700">{z.alertCount}</td>
                        <td className="py-2 px-3 text-right text-slate-700">{z.violationCount}</td>
                        <td className="py-2 px-3 text-right text-slate-700">{z.dailyAlertRate}</td>
                        <td className="py-2 px-3 text-right font-bold text-slate-800">{z.riskAdjustedScore}</td>
                      </tr>
                    ))}
                    {(!zoneData?.zones || zoneData.zones.length === 0) && (
                      <tr>
                        <td colSpan={8} className="py-8 text-center text-slate-400">No zone data available</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Contractor Comparison Tab */}
      {activeTab === 'contractors' && (
        <div>
          <div className="glass p-4">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500">Rank</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500">Contractor</th>
                    <th className="text-right py-2 px-3 text-xs font-semibold text-slate-500">Safety Rating</th>
                    <th className="text-right py-2 px-3 text-xs font-semibold text-slate-500">Workers</th>
                    <th className="text-right py-2 px-3 text-xs font-semibold text-slate-500">Alerts</th>
                    <th className="text-right py-2 px-3 text-xs font-semibold text-slate-500">Rate/Worker</th>
                    <th className="text-center py-2 px-3 text-xs font-semibold text-slate-500">Grade</th>
                  </tr>
                </thead>
                <tbody>
                  {(contractorData?.contractors || []).map((c, i) => (
                    <tr key={c.contractor.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                      <td className="py-2 px-3 font-bold text-slate-400">#{i + 1}</td>
                      <td className="py-2 px-3">
                        <p className="font-medium text-slate-700">{c.contractor.name}</p>
                      </td>
                      <td className="py-2 px-3 text-right font-bold text-slate-700">{c.safetyRating}</td>
                      <td className="py-2 px-3 text-right text-slate-600">{c.workerCount}</td>
                      <td className="py-2 px-3 text-right text-slate-700">{c.alertCount}</td>
                      <td className="py-2 px-3 text-right font-medium text-slate-700">{c.normalizedRate}</td>
                      <td className="py-2 px-3 text-center"><GradeBadge grade={c.grade} /></td>
                    </tr>
                  ))}
                  {(!contractorData?.contractors || contractorData.contractors.length === 0) && (
                    <tr>
                      <td colSpan={7} className="py-8 text-center text-slate-400">No contractor data available</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
